import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { VirtuosoGrid, VirtuosoGridHandle } from 'react-virtuoso';

import Button from '@/core/components/Button';
import type { CatalogueSearchSchema } from '@/core/components/CatalogueSearch/CatalogueSearchProvider';
import { useCatalogueSearchContext } from '@/core/components/CatalogueSearch/CatalogueSearchProvider';
import DonationCard from '@/core/components/DonationCard';
import Icons from '@/core/components/Icons';
import { useIsLg } from '@/core/hooks/useMediaQuery';
import { useComponentsContext } from '@/core/lib/components/components.context';
import { TEMPORARY_LIST_ITEM_ADD_POSITION } from '@/core/lib/constants';
import { useNativeContext } from '@/core/lib/native/native.context';
import type DonationDomain from '@/core/lib/new-architecture/domain/donation.domain';
import Store from '@/core/lib/new-architecture/store';
import { usePersistContext } from '@/core/lib/persist/persist.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

interface ItemListProps {
  donations: DonationDomain[];
  search: CatalogueSearchSchema;
  next: () => void;
}

const persistKeyForDonationToScrollOver = 'selectedDonationId';

const VirtuosoFooter = ({ context: { isLoading } }: { context: { isLoading: boolean } }) => (
  <div className="flex justify-center pb-20">{isLoading ? <Icons icon="spinner" size="20" color="button-default" weight="bold" /> : null}</div>
);

const ItemList: React.FC<ItemListProps> = ({ donations, search, next }) => {
  const { t } = useTranslationContext(['common', 'catalogue']);
  const { isNative } = useNativeContext();
  const { onOpen } = useCatalogueSearchContext();
  const isLg = useIsLg();
  const { Refresher } = useComponentsContext();
  const { setLocal, getLocal, removeLocal } = usePersistContext();
  const { total, refetchDonations, isLoading } = Store.catalogue.useCatalogue();
  const [localScrollTop, setLocalScrollTop] = useState<number>(0);
  const [isVirtuosoReady, setIsVirtuosoReady] = useState(false);
  const virtuosoRef = useRef<VirtuosoGridHandle>(null);
  const { data: current } = Store.current.useCurrent();

  useEffect(() => {
    getLocal(persistKeyForDonationToScrollOver).then(async initialTopMostItemIndexFromPersistContext => {
      if (isVirtuosoReady && virtuosoRef.current && initialTopMostItemIndexFromPersistContext) {
        virtuosoRef.current.scrollToIndex({ index: Number(initialTopMostItemIndexFromPersistContext) });
        await removeLocal(persistKeyForDonationToScrollOver);
      }
    });
  }, [isVirtuosoReady, virtuosoRef]);

  const { renderWebList, renderApp } = useMemo(() => {
    const renderWebList: React.ReactNode[] = [];
    let contentAdNumber = 0;

    if (!isNative) {
      donations.forEach((donation, index) => {
        renderWebList.push(
          <div key={donation.getId()} className="col-span-1">
            <DonationCard donation={donation} vertical />
          </div>,
        );

        const showListItemAd = index > 0 && (index + 1) % TEMPORARY_LIST_ITEM_ADD_POSITION === 0;

        if (showListItemAd) {
          const key = isLg ? `${donation.getId()}-ad-desktop` : `${donation.getId()}-ad-mobile`;
          contentAdNumber++;
          renderWebList.push(
            <React.Fragment key={key}>
              <div className="col-span-2 mx-auto flex w-full items-center justify-center lg:col-span-4 lg:justify-between">
                <div className="hidden min-h-[310px] w-[340px] lg:block" dangerouslySetInnerHTML={{ __html: `<div class="Content_${contentAdNumber}" style="display:none;"></div>` }}></div>
                <div className="hidden min-h-[310px] w-[340px] lg:block" dangerouslySetInnerHTML={{ __html: `<div class="Content_${contentAdNumber + 1}" style="display:none;"></div>` }}></div>
                <div className="min-h-[310px] w-[320px] lg:hidden" dangerouslySetInnerHTML={{ __html: `<div class="Mobile_Pos${contentAdNumber}" style="display:none;"></div>` }}></div>
              </div>
            </React.Fragment>,
          );
          if (isLg) {
            contentAdNumber++;
          }
        }
      });
    }

    return {
      renderWebList,
      renderApp: isNative ? (
        <Refresher
          scrollY={false}
          isDisable={typeof refetchDonations !== 'function' || localScrollTop > 0}
          handleRefresh={async event => {
            await refetchDonations?.();
            event.detail.complete();
          }}
        >
          <VirtuosoGrid
            ref={virtuosoRef}
            data={donations}
            totalCount={total}
            context={{ isLoading }}
            listClassName="grid grid-cols-2 gap-2 px-4"
            endReached={() => next()}
            readyStateChanged={ready => {
              setIsVirtuosoReady(ready);
            }}
            components={{
              Footer: VirtuosoFooter,
            }}
            stateChanged={({ scrollTop }) => setLocalScrollTop(scrollTop)}
            itemContent={(index, donation) => (
              //  eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <div tabIndex={-1} id={donation.getId().toString()} key={donation.getId()} onClick={async () => await setLocal(persistKeyForDonationToScrollOver, index.toString())} role="button">
                <DonationCard donation={donation} vertical />
              </div>
            )}
          />
        </Refresher>
      ) : null,
    };
  }, [donations, isLg, isNative, isLoading, virtuosoRef, current]);

  // handle reset scroll when new search is made on web
  const prevSearch = useRef<CatalogueSearchSchema | null>(null);
  useEffect(() => {
    if (prevSearch.current === null) {
      prevSearch.current = search;
    } else if (!isEqual(prevSearch.current, search)) {
      prevSearch.current = search;
      window.document.scrollingElement?.scrollTo({ top: 0 });
    }
  }, [search, prevSearch]);

  if (donations.length === 0) {
    return (
      <div className="mt-5 flex w-full justify-center">
        <div className="flex h-full w-100 flex-col items-center justify-center">
          <img src="/assets/empty/search.svg" className="size-45" alt={t('not-found.title', { ns: 'catalogue' })} />
          <h2 className="text-body-secondary mb-2 font-semibold text-content-primary">{t('not-found.title', { ns: 'catalogue' })}</h2>
          <p className="text-body-secondary mb-7 text-center font-normal text-content-placeholder">{t('not-found.subtitle', { ns: 'catalogue' })}</p>
          <div className="flex w-full flex-col space-y-20">
            <Button variant="secondary" onClick={onOpen} iconLeft="sliders">
              {t('not-found.again', { ns: 'catalogue' })}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return renderApp ?? <div className="mt-5 grid w-full grid-cols-2 gap-x-3 gap-y-5 lg:grid-cols-4">{renderWebList}</div>;
};

export default ItemList;
